import { useEffect, useState, useCallback } from 'react';
import { Badge, Alert, Button, Card } from '@dreamtree-org/twreact-ui';
import { Copy, Check, Download } from 'lucide-react';
import { useStableApi } from '@/lib/api';
import Can from '@rbac/Can';
import { writeActionFor } from '@rbac/writeAction';
import DataTable from '@/components/DataTable';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * "Browser Devices" (`/app-browser-device`) — pair a Chrome profile the agents may drive,
 * and decide what it is allowed to touch.
 *
 * Rows come from routes/browserDevicesRouter.js, not generic CRUD: AppBrowserDevice's
 * writes are unseeded (deny-by-omission) because pairing mints a one-time code that lives
 * in Redis and a token hash that must never be writable.
 *
 * The screen is deliberately blunt about the allowlist. An empty list means the agent can
 * do nothing on that device — services/browser/deviceAuth.js denies on empty, inverting
 * the embed-key default — and a device sitting there doing nothing with no explanation is
 * the most likely way for this to look broken. So an empty list is called out in the row,
 * not left as a blank cell.
 */

const INSTALL_STEPS = [
  'Download the package below and unzip it somewhere permanent — Chrome loads it from that folder every time it starts, so a Downloads folder you clear out will break it.',
  'Open chrome://extensions and turn on "Developer mode" (top right).',
  'Click "Load unpacked" and choose the unzipped folder.',
  'Open the extension\'s options page and enter the pairing code below.',
];

function CopyButton({ value, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size="sm"
      variant="secondary"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard unavailable — the text is still selectable */ }
      }}
    >
      {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
      <span className="ml-1">{copied ? 'Copied' : label}</span>
    </Button>
  );
}

function StatusBadge({ device }) {
  if (device.status === 'revoked') return <Badge variant="error" size="sm">revoked</Badge>;
  if (device.connectedHere) return <Badge variant="success" size="sm">connected</Badge>;
  return <Badge variant="secondary" size="sm">idle</Badge>;
}

/** The allowlist editor. One origin per line — a textarea rather than a tag input because
 *  operators paste these from a list, and the server validates each line anyway. */
function OriginsEditor({ device, onSave, saving }) {
  const [text, setText] = useState((device.allowedOrigins || []).join('\n'));
  const dirty = text !== (device.allowedOrigins || []).join('\n');
  return (
    <div className="space-y-2">
      <label className="block text-xs text-[var(--app-muted)]" htmlFor={`origins-${device.id}`}>
        Sites this browser may be driven on — one per line
        (<code>https://app.example.com</code> or <code>*.example.com</code>)
      </label>
      <textarea
        id={`origins-${device.id}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        spellCheck={false}
        className="w-full rounded border border-[var(--app-border)]/60 bg-[var(--app-surface)] p-2 font-mono text-xs text-[var(--app-text)]"
      />
      <Can module="browser" action="drive">
        <Button
          size="sm"
          disabled={!dirty || saving}
          onClick={() => onSave(device.id, text.split('\n').map((l) => l.trim()).filter(Boolean))}
        >
          {saving ? 'Saving…' : 'Save sites'}
        </Button>
      </Can>
    </div>
  );
}

/** Static — nothing here depends on component state, and defining it inside the
 *  component would rebuild every cell renderer on each render. */
const DEVICE_COLUMNS = [
  { key: 'name', label: 'Browser' },
  { key: 'status', label: 'Status', render: (row) => <StatusBadge device={row} /> },
  {
    key: 'allowedOrigins',
    label: 'Allowed sites',
    render: (row) => (row.allowedOrigins && row.allowedOrigins.length
      ? <span className="text-xs">{row.allowedOrigins.join(', ')}</span>
      : <span className="text-xs text-[var(--app-muted)]">none — this browser cannot be driven yet</span>),
  },
  {
    key: 'capabilities',
    label: 'Full-page capture',
    render: (row) => (row.capabilities && row.capabilities.debugger ? 'yes' : 'no'),
  },
  { key: 'extensionVersion', label: 'Version', render: (row) => row.extensionVersion || '—' },
  {
    key: 'lastSeenAt',
    label: 'Last seen',
    render: (row) => (row.lastSeenAt ? new Date(row.lastSeenAt).toLocaleString() : 'never'),
  },
];

/** The install instructions and the download. Gated on `pair`: the zip is what turns a
 *  login into a browser the platform can drive. */
function InstallCard({ version }) {
  return (
    <Can module="browser" action="pair">
      <Card className="mb-4 p-4">
        <h3 className="mb-2 text-sm font-semibold">Install the extension</h3>
        <ol className="ml-4 list-decimal space-y-1 text-sm text-[var(--app-muted)]">
          {INSTALL_STEPS.map((step) => <li key={step}>{step}</li>)}
        </ol>
        <p className="mt-3 text-xs text-[var(--app-muted)]">
          Chrome has not allowed websites to install extensions directly since 2018, so this
          is the Load-unpacked route. It does not auto-update — the extension tells the
          operator when a newer version is available here.
        </p>
        <div className="mt-3 flex items-center gap-3">
          {/* A real anchor, NOT <Button as="a">. twreact-ui's Button hardcodes a <button>
              element and has no `as` prop, so `as="a"` + href rendered a button carrying a dead
              href — clicking did nothing, with no error to explain it. The classes below are the
              ones Button's own primary/md variant applies, so this looks identical. */}
          <a
            href="/api/browser-devices/download"
            download
            className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary-600 px-4 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            <Download size={16} />
            Download extension{version ? ` v${version}` : ''}
          </a>
        </div>
      </Card>
    </Can>
  );
}

/** A live code with its countdown. Shown once and never re-fetchable — claiming it
 *  deletes it server-side — so the screen never pretends it can be recovered. */
function PairingCode({ pairing }) {
  const mins = Math.floor(pairing.secondsLeft / 60);
  const secs = String(pairing.secondsLeft % 60).padStart(2, '0');
  return (
    <Can module="browser" action="pair">
      <Alert variant="warning" className="mb-4" title="Pairing code — good for one browser, once">
        <div className="flex items-center gap-3">
          <code className="font-mono text-lg tracking-widest">{pairing.code}</code>
          <CopyButton value={pairing.code} />
          <span className="text-xs text-[var(--app-muted)]">expires in {mins}:{secs}</span>
        </div>
      </Alert>
    </Can>
  );
}

export default function BrowserDevicesPage() {
  const api = useStableApi();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pairing, setPairing] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [extensionVersion, setExtensionVersion] = useState(null);

  // Empty deps are load-bearing: useStableApi's identity never changes, so `load` is built
  // once. Depending on the api object here (or on useAppApi's per-render object) made the
  // effect below re-fire on every render, which setLoading/setDevices re-triggered — an
  // unbounded fetch loop. See lib/api.js for the full account.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.fetchApi('browserDeviceList');
      setDevices(res.devices || []);
      setExtensionVersion(res.extensionVersion || null);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // A code is live for ten minutes and works once, so the screen counts it down rather
  // than leaving a dead string on screen that looks like it should still work.
  useEffect(() => {
    if (!pairing) return undefined;
    const timer = setInterval(() => {
      setPairing((p) => (p && p.secondsLeft > 1 ? { ...p, secondsLeft: p.secondsLeft - 1 } : null));
    }, 1000);
    return () => clearInterval(timer);
  }, [pairing]);

  async function mintCode() {
    try {
      const res = await api.fetchApi('browserPairingCode', {});
      setPairing({ code: res.code, secondsLeft: res.expiresInSeconds });
    } catch (err) {
      setError(err.message);
    }
  }

  async function saveOrigins(id, allowedOrigins) {
    setSavingId(id);
    try {
      await api.fetchApi('browserDeviceUpdate', { id, allowed_origins: allowedOrigins });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingId(null);
    }
  }

  async function revoke(device) {
    if (!window.confirm(`Revoke "${device.name}"? It stops being drivable immediately and has to be paired again.`)) return;
    try {
      await api.fetchApi('browserDeviceRevoke', device.id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }


  const Details = useCallback(({ row }) => (
    <div className="space-y-3 p-2">
      <OriginsEditor device={row} onSave={saveOrigins} saving={savingId === row.id} />
      {row.status !== 'revoked' && (
        <Can module="browser" action="pair">
          <Button size="sm" variant="danger" onClick={() => revoke(row)}>Revoke this browser</Button>
        </Can>
      )}
    </div>
  // Deps deliberately narrow: saveOrigins/revoke are rebuilt every render, and including
  // them would remount the expanded panel on each one and lose a half-typed origin list.
  // That remount is the same trap EmbedKeysPage.jsx:415 documents for its Details memo.
  ), [savingId]);

  return (
    <PageShell>
      <PageHeader
        title="Browser Devices"
        subtitle="Chrome profiles your agents may read and act in — one site at a time, with the operator's consent."
        actions={(
          <Can module="browser" action={writeActionFor('browser')}>
            <Button onClick={mintCode}>Pair a browser</Button>
          </Can>
        )}
      />

      {error && <Alert variant="error" className="mb-4">{error}</Alert>}

      <Alert variant="info" className="mb-4" title="What a paired browser can do">
        An agent acts inside the signed-in session of whoever installed the extension. It is
        limited three times over: Chrome only grants the sites that person approves, this
        screen's allowlist bounds it again, and anything that changes a page waits for a
        person to click Allow. A browser with no sites listed can do nothing at all.
      </Alert>

      {pairing && <PairingCode pairing={pairing} />}

      <InstallCard version={extensionVersion} />

      <DataTable
        data={devices}
        columns={DEVICE_COLUMNS}
        loading={loading}
        emptyText="No browsers paired yet. Pair one to let an agent work inside a real signed-in session."
        hasDetails
        DetailsComponent={Details}
        onReload={load}
      />
    </PageShell>
  );
}
